import classNames from 'classnames';
import CustomLoader from 'components/molecules/CustomLoader.component';
import AppBannersProvider, {
  AppBannersContext,
} from 'contexts/AppBannersProvider';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useTransactionsUpdateEvent from 'hooks/Bank/useTransactionsUpdateEvent';
import {
  useGetWorldThemeFromPath,
  worldThemeContext,
} from 'hooks/shared/useWorldTheme';
import { useGrowthABExperimentsState } from 'hooks/useAssignGrowthABGroups';
import UseGratifications from 'hooks/useGratifications';
import usePeppolDeactiveEvent from 'hooks/usePeppolDeactiveEvent';
import usePeppolIntegrationAutoUpdate from 'hooks/usePeppolIntegrationAutoUpdate';
import UseRevenueStatusEvent from 'hooks/useRevenueStatusEvent';
import type { FC, ReactNode } from 'react';
import { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useParams } from 'react-router';
import ResizeObserver from 'resizeObserver';
import type { GrowthExperimentsWithABGroupsType } from 'store/ABExperiments.store';
import type { NavbarNavigationListType } from 'types/navbar.types';
import type { RoutesListType, RouteType } from 'types/routing.types';
import type { IUser } from 'types/users.types';
import { isExpert } from 'utils/constants';
import BelgiumPeppolVerificationBanner from '../BelgiumPeppolVerificationBanner';
import BottomTabs from '../BottomTabs';
import PendingAccessRequestsBanner from '../PendingAccessRequestsBanner';
import Sidebar from '../Sidebar';
import TopPanel from '../TopPanel';
import TrialBanner from '../TrialBanner';
import PeppolAccountantRegisteredBanner from 'components/pages/Revenues/PeppolAccountantRegisteredBanner.component';
import layoutContext from './layoutContext';
import useCleanExpensesFileStore from 'hooks/Expenses/filesUploadHooks/useCleanExpensesFileStore';
import useCleanRepositoryFileStore from 'hooks/DocumentsRepository/filesUploadHooks/useCleanRepositoryFileStore';
import useCleanRevenuesFileStore from 'hooks/Revenues/filesUploadHooks/useCleanRevenuesFileStore';

type LayoutProps = {
  children: ReactNode;
  navigationListGenerator: (
    customerId: string,
    experiments?: GrowthExperimentsWithABGroupsType,
    user?: IUser,
  ) => NavbarNavigationListType;
  showBgImage?: boolean;
  className?: string;
};

const Banners: FC = () => {
  const {
    displayTRIALBanner,
    displayBelgiumPeppolVerificationBanner,
    displayPeppolAccountantRegisteredBanner,
    displayPendingAccessRequestsBanner,
  } = useContext(AppBannersContext);

  if (displayPendingAccessRequestsBanner)
    return <PendingAccessRequestsBanner />;

  if (displayTRIALBanner) return <TrialBanner />;

  if (displayBelgiumPeppolVerificationBanner) {
    return <BelgiumPeppolVerificationBanner />;
  }

  if (displayPeppolAccountantRegisteredBanner) {
    return <PeppolAccountantRegisteredBanner />;
  }

  return null;
};

export const assignLayoutToRoutes = (
  routes: RoutesListType,
  navigationGenerator: (
    customerId: string,
    experiments?: GrowthExperimentsWithABGroupsType,
  ) => NavbarNavigationListType,
): RoutesListType =>
  routes.map((route: RouteType) => ({
    ...route,
    Component: route.withLayout ? (
      <Layout
        navigationListGenerator={navigationGenerator}
        showBgImage={route?.showBgImage}
        className={route?.className}
      >
        {route.Component}
      </Layout>
    ) : (
      <Suspense fallback={<CustomLoader className="h-screen" />}>
        {route.Component}
      </Suspense>
    ),
  }));

const Content: FC<Omit<LayoutProps, 'navigationListGenerator'>> = ({
  children,
  className = 'px-8',
}) => {
  const { layoutBGColor } = useContext(layoutContext);

  const isMobileOrTablet = isMobile || isTablet;

  return (
    <div className={`${layoutBGColor} ml-[var(--sidebar-width)]`}>
      <div className="mx-auto max-w-[1750px] flex min-h-screen">
        <div
          id="LAYOUT_CONTENT"
          className={classNames(
            'py-8 flex flex-col flex-1 min-h-full overflow-x-hidden',
            className,
            {
              'pt-32': isExpert,
              'pb-20': isMobileOrTablet,
            },
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

const Layout: FC<LayoutProps> = ({
  children,
  navigationListGenerator,
  showBgImage = true,
  className = 'px-8',
}) => {
  const [shouldShowBgImage, setShouldShowBgImage] = useState(showBgImage);
  const [sidebarArea, setSidebarArea] = useState(176);
  const [layoutBGColor, setLayoutBGColor] = useState('transparent');
  const { id: customerId } = useParams();
  const { experiments } = useGrowthABExperimentsState();

  const { data: user } = useGetCurrentCustomerData();
  const navigationList: NavbarNavigationListType = navigationListGenerator(
    customerId as string,
    experiments,
    user,
  );
  const theme = useGetWorldThemeFromPath();

  useTransactionsUpdateEvent();
  usePeppolIntegrationAutoUpdate();
  usePeppolDeactiveEvent();
  useCleanExpensesFileStore();
  useCleanRevenuesFileStore();
  useCleanRepositoryFileStore();

  const sidebarRef = useRef();

  useEffect(() => {
    if (!sidebarRef.current) return;
    const resizeObserver = new ResizeObserver((d) => {
      const { contentRect } = d[0] || {};

      setSidebarArea(contentRect?.width + contentRect?.left + 16);
    });

    resizeObserver.observe(sidebarRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const mainRef = useRef<HTMLDivElement>(null);
  const sidebarTop = mainRef.current?.offsetTop || 0;

  return (
    <worldThemeContext.Provider value={theme}>
      <layoutContext.Provider
        value={{
          shouldShowBgImage,
          setShouldShowBgImage,
          setLayoutBGColor,
          layoutBGColor,
          sidebarArea,
        }}
      >
        <UseGratifications />
        <UseRevenueStatusEvent />
        <main className="min-h-full" ref={mainRef}>
          <Sidebar
            navigationList={navigationList}
            sidebarTop={sidebarTop}
            sidebarRef={sidebarRef}
          />

          <Suspense fallback={<CustomLoader className="h-screen" />}>
            <AppBannersProvider>
              {isExpert ? <TopPanel /> : null}

              <Banners />

              <Content className={className} showBgImage={showBgImage}>
                {children}
              </Content>
            </AppBannersProvider>
          </Suspense>

          <BottomTabs />
        </main>
      </layoutContext.Provider>
    </worldThemeContext.Provider>
  );
};

export default Layout;
